import React, { useState } from 'react';
import { Tag, Typography, Button, Popconfirm, theme } from 'antd';
import { EditOutlined, DeleteOutlined, CheckCircleFilled, CalendarOutlined, SendOutlined } from '@ant-design/icons';
import { IconHeart, IconHeartFilled, IconPhoto, IconChevronLeft, IconChevronRight, IconStarFilled, IconClock, IconTool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { message } from '../../services/toastMessage';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES, HEAVY_DUTY_MACHINERY_SPACE_STATUS_LABEL_KEYS, MACHINERY_LIVE_STATUS_TONES, MACHINERY_LIVE_STATUS_LABEL_KEYS, pillToneStyle } from '../../theme/creamyTokens';
import { isPublicMachineryTelemetry } from '../../interfaces/heavyDutyMachinery/ITelemetry';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useUserProfile } from '../../contextProviders/UserProfileProvider';
import { useFxConvertedAmount } from '../../hooks/useFxConvertedAmount';
import { addFavorite, removeFavorite } from '../../services/FavoriteService';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';

const { Title, Text } = Typography;

interface HeavyDutyMachineryCardProps {
  space: IHeavyDutyMachinery;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onManageAvailability?: () => void;
  onPublish?: () => void;
  /** Host only: "this one came back broken". Opens the modal in the parent. */
  onTakeOutOfService?: () => void;
  /** Host only: repaired, or parked by mistake. */
  onBackInService?: () => void;
  // Only set by the landing page's "Near you" shelf (computed client-side
  // from the browser's geolocation) -- nothing else knows the viewer's
  // position, so every other caller leaves this undefined.
  distanceKm?: number;
  // Minimal photo+title+price rendering for tight spaces (currently only
  // the map's pin-click popup) -- no favorite/photo-nav/rating/status,
  // none of which fit a popup anchored to a single pin.
  compact?: boolean;
  // Set by /explore when this card's pin is hovered on the map, so the
  // same scale+shadow the card gets from a direct mouse hover also shows
  // when the hover originates from the map instead.
  forceHover?: boolean;
  // Only set by HeavyDutyMachineryFavorites -- called after a successful un-favorite
  // so that list can drop the card immediately instead of waiting for the
  // next full refetch. No effect anywhere else this card is used.
  onUnfavorited?: () => void;
}

const PUBLISHABLE_STATUSES = new Set(['Draft', 'PendingImprovement']);

/** Airbnb-style listing card: big photo, favorite/photo-count overlays, title+rating, then price. */
const HeavyDutyMachineryCard: React.FC<HeavyDutyMachineryCardProps> = ({
  space,
  onClick,
  onEdit,
  onDelete,
  onManageAvailability,
  onPublish,
  onTakeOutOfService,
  onBackInService,
  distanceKm,
  compact,
  forceHover,
  onUnfavorited,
}) => {
  const { t, i18n } = useTranslation();
  const { resolveName: resolveCategoryName } = useTaxonomy();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { userProfile } = useUserProfile();
  // Seeded from space.isFavorite (computed server-side at read time, same as
  // averageRating) -- tracked locally from there so a tap can flip it
  // immediately instead of waiting on a re-fetch of the whole list.
  const [favorite, setFavorite] = useState(space.isFavorite);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [deleteSheetOpen, setDeleteSheetOpen] = useState(false);

  // Draft/PendingVerification/Active/... only matters to the host managing
  // their own listing (this card's management actions are only ever passed
  // from "My spaces") -- a guest browsing already knows it's listed, so
  // showing e.g. "Active" to them is just noise.
  const isOwnerView = !!(onEdit || onDelete || onManageAvailability || onPublish);
  // The window can have passed without anybody touching the row -- the
  // machine comes back by itself (see Machinery.OutOfServiceUntil), so the
  // date is what decides, never the mere presence of outOfServiceSince.
  const isParked =
    !!space.outOfServiceSince &&
    (!space.outOfServiceUntil || dayjs(space.outOfServiceUntil).isAfter(dayjs()));
  const photos = space.photos?.length ? space.photos : space.photoThumbnails ?? [];
  const hasMultiplePhotos = photos.length > 1;
  const activePhoto = photos[photoIndex] ?? photos[0];

  // Host's own currency -- see currencyHelper's own doc comment: this shows
  // what the host actually priced the machine at, same as the checkout
  // widget's live estimates before a real guest-currency preference exists.
  const formatPrice = (amount?: number): string | undefined =>
    amount == null ? undefined : formatCurrency(amount, space.hostCurrencyCode, i18n.language);
  const rawPriceAmount =
    space.rentalMode === 'Overnight'
      ? space.pricePerNight
      : space.rentalMode === 'DayPass'
        ? space.pricePerDayPass
        : space.pricePerHour ?? space.pricePerNight;
  const priceLabel =
    space.rentalMode === 'Overnight'
      ? formatPrice(space.pricePerNight)
      : space.rentalMode === 'DayPass'
        ? formatPrice(space.pricePerDayPass)
        : formatPrice(space.pricePerHour) || formatPrice(space.pricePerNight);
  // Secondary, smaller/greyer approximation shown next to the host's own
  // price above -- never a substitute for it, only meaningful for a guest
  // (not the host managing their own listing) whose own preference differs
  // from this machine's HostCurrencyCode. See useFxConvertedAmount/
  // FxRateController's own doc comments for why this is display-only.
  const convertedPriceAmount = useFxConvertedAmount(rawPriceAmount, space.hostCurrencyCode, userProfile?.preferredCurrencyCode);
  const convertedPriceLabel =
    convertedPriceAmount != null && userProfile?.preferredCurrencyCode
      ? formatCurrency(convertedPriceAmount, userProfile.preferredCurrencyCode, i18n.language)
      : undefined;
  const priceUnit =
    space.rentalMode === 'Overnight'
      ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_NIGHT)
      : space.rentalMode === 'DayPass'
        ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_DAY_PASS)
        : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR);

  const brandModel = [space.manufacturerBrand, space.modelName].filter(Boolean).join(' ');
  const hasWetHire = space.wetHireHostOperatorAvailable || space.wetHireThirdPartyAvailable;

  // Only set by searchHeavyDutyMachineries when the search itself carried an
  // availability filter (Now/+2h/+3h/Custom, or Overnight's date range) --
  // end is already capped for display server-side, see
  // AvailabilityCalculator.GetNextAvailableSlot.
  const slot = space.nextAvailableSlot;
  const availabilityLabel = slot
    ? slot.isOpenEnded
      ? `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_AVAILABLE_FROM_LABEL)} ${dayjs(slot.start).format('HH:mm')}`
      : `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_AVAILABLE_LABEL)} ${dayjs(slot.start).format('HH:mm')}-${dayjs(slot.end).format('HH:mm')}`
    : undefined;

  // Optimistic: flips immediately, then reverts + toasts if the request
  // fails -- same "anyone can browse, identity only required for the
  // actual action" login gate HeavyDutyMachineryBookingWidget uses for booking.
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      message.info(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITE_LOGIN_REQUIRED));
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      navigate('/login');
      return;
    }
    if (favoriteBusy) return;

    const next = !favorite;
    setFavorite(next);
    setFavoriteBusy(true);
    try {
      if (next) await addFavorite(space.id);
      else {
        await removeFavorite(space.id);
        onUnfavorited?.();
      }
    } catch {
      setFavorite(!next);
      message.error(
        translate(t, next ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITE_ADD_ERROR : TRANSLATIONS.HEAVY_DUTY_MACHINERY_FAVORITE_REMOVE_ERROR)
      );
    } finally {
      setFavoriteBusy(false);
    }
  };

  const showPrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
  };
  const showNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoIndex((i) => (i + 1) % photos.length);
  };

  if (compact) {
    return (
      <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', width: 200 }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 10',
            borderRadius: 12,
            overflow: 'hidden',
            background: token.colorFillTertiary,
          }}
        >
          {activePhoto ? (
            <img src={activePhoto} alt={space.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: token.colorTextQuaternary,
              }}
            >
              <IconPhoto size={24} />
            </div>
          )}
        </div>
        <div style={{ paddingTop: 8 }}>
          <Text strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {space.title}
          </Text>
          <Text style={{ fontSize: 13 }}>{priceLabel ? `${priceLabel} ${priceUnit}` : '—'}</Text>
        </div>
      </div>
    );
  }

  // Enlarged from the original 26px to a full 44px touch target -- these
  // float over the photo with nothing else competing for that space, so
  // growing the visible circle itself (rather than an invisible hit area
  // around a smaller one) reads fine here, unlike the tighter timeline
  // handles. Opacity still only applies on hover-capable devices -- the
  // `heavyDutyMachinery-card-nav-btn` class (see index.css) forces it to 1 under
  // `(hover: none)` so touch devices always see them, since there's no
  // hover gesture to reveal them with otherwise.
  const navButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'rgba(255,255,255,0.9)',
    borderRadius: '50%',
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: hovering ? 1 : 0,
    transition: 'opacity 0.15s',
  };

  return (
    <div
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <motion.div
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        animate={{
          scale: hovering || forceHover ? 1.02 : 1,
          boxShadow: hovering || forceHover ? '0 10px 24px rgba(0,0,0,0.18)' : '0 0px 0px rgba(0,0,0,0)',
        }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 10',
          borderRadius: 16,
          overflow: 'hidden',
          background: token.colorFillTertiary,
        }}
      >
        {activePhoto ? (
          <img src={activePhoto} alt={space.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: token.colorTextQuaternary }}>
            <IconPhoto size={32} />
          </div>
        )}

        <motion.button
          type="button"
          aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_FAVORITE_LABEL)}
          aria-pressed={favorite}
          onClick={handleToggleFavorite}
          whileTap={{ scale: 1.3 }}
          transition={{ type: 'spring', stiffness: 500, damping: 15 }}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            border: 'none',
            background: 'rgba(0,0,0,0.35)',
            borderRadius: '50%',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {favorite ? <IconHeartFilled size={18} color={token.colorError} /> : <IconHeart size={18} color="#fff" />}
        </motion.button>

        {availabilityLabel && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: token.colorBgContainer,
              border: '1px solid rgba(0,0,0,0.12)',
              boxShadow: '0 4px 10px rgba(0,0,0,0.14)',
              borderRadius: 999,
              fontWeight: 400,
              fontSize: 12,
              color: token.colorText,
              padding: '4px 10px',
            }}
          >
            <IconClock size={12} style={{ position: 'relative', top: 0.5, flexShrink: 0 }} />
            <span>{availabilityLabel}</span>
          </div>
        )}

        {hasMultiplePhotos && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: 10,
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                borderRadius: 12,
                padding: '2px 8px',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <IconPhoto size={12} /> {photoIndex + 1} / {photos.length}
            </div>
            <button
              type="button"
              aria-label="previous-photo"
              className="heavyDutyMachinery-card-nav-btn"
              onClick={showPrevPhoto}
              style={{ ...navButtonStyle, left: 8 }}
            >
              <IconChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="next-photo"
              className="heavyDutyMachinery-card-nav-btn"
              onClick={showNextPhoto}
              style={{ ...navButtonStyle, right: 8 }}
            >
              <IconChevronRight size={18} />
            </button>
          </>
        )}
      </motion.div>

      <div style={{ paddingTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {space.telemetry && isPublicMachineryTelemetry(space.telemetry) && (
          <span
            style={{
              display: 'inline-block',
              alignSelf: 'flex-start',
              marginBottom: 4,
              padding: '2px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              ...pillToneStyle(MACHINERY_LIVE_STATUS_TONES[space.telemetry.status] || 'default', token),
            }}
          >
            {translate(t, MACHINERY_LIVE_STATUS_LABEL_KEYS[space.telemetry.status] ?? space.telemetry.status)}
          </span>
        )}
        {isOwnerView && (
          <span
            style={{
              display: 'inline-block',
              alignSelf: 'flex-start',
              marginBottom: 4,
              padding: '2px 10px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              ...pillToneStyle(HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES[space.status] || 'default', token),
            }}
          >
            {translate(t, HEAVY_DUTY_MACHINERY_SPACE_STATUS_LABEL_KEYS[space.status] ?? space.status)}
          </span>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <Title level={5} style={{ margin: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {space.title}
          </Title>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
              padding: '2px 8px',
              borderRadius: 999,
              background: token.colorFillTertiary,
            }}
          >
            <IconStarFilled size={12} />
            <Text strong style={{ fontSize: 12 }}>
              {space.averageRating != null ? space.averageRating.toFixed(1) : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_NEW_BADGE)}
            </Text>
          </div>
        </div>

        <Text type="secondary" style={{ fontSize: 13 }}>
          {resolveCategoryName(space.category)}
          {brandModel && ` · ${brandModel}`} · {space.city}
          {distanceKm != null && ` · ${distanceKm.toFixed(0)} km`}
        </Text>

        {(space.weightTons != null || space.powerHp != null) && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {[
              space.weightTons != null && `${space.weightTons}t`,
              space.powerHp != null && `${space.powerHp}hp`,
            ].filter(Boolean).join(' · ')}
          </Text>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {space.verifiedBadge && (
            <Tag color="blue" icon={<CheckCircleFilled />} style={{ margin: 0 }}>
              {translate(t, TRANSLATIONS.HOST_VERIFICATION_VERIFIED)}
            </Tag>
          )}
          {hasWetHire && (
            <Tag color="green" style={{ margin: 0 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_WET_HIRE_BADGE)}
            </Tag>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 8 }}>
          <Text strong style={{ fontSize: 15 }}>
            {priceLabel ? `${priceLabel} ${priceUnit}` : '—'}
          </Text>
          {!isOwnerView && convertedPriceLabel && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
              (≈ {convertedPriceLabel})
            </Text>
          )}
        </div>

        {(onEdit || onDelete || onManageAvailability || onPublish || onTakeOutOfService) && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* First, above everything else, and only while it applies. A
                parked machine is the one fact about this card that changes
                what the host should do next. */}
            {isParked && (
              <div
                style={{
                  borderRadius: 14,
                  padding: '10px 12px',
                  background: token.colorWarningBg,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: token.colorWarningText, fontSize: 13 }}>
                  <IconTool size={15} />
                  {space.outOfServiceUntil
                    ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_BADGE_UNTIL, {
                        date: dayjs(space.outOfServiceUntil).format('D MMM'),
                      })
                    : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_BADGE)}
                </span>
                {space.outOfServiceReason && (
                  <span style={{ fontSize: 12, color: token.colorTextSecondary }}>{space.outOfServiceReason}</span>
                )}
                {onBackInService && (
                  <Button shape="round" size="small" style={{ alignSelf: 'flex-start' }} onClick={onBackInService}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BACK_IN_SERVICE_ACTION)}
                  </Button>
                )}
              </div>
            )}
            {onPublish && PUBLISHABLE_STATUSES.has(space.status) && (
              <Popconfirm
                title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_CONFIRM)}
                onConfirm={onPublish}
                okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_BUTTON)}
                cancelText={translate(t, TRANSLATIONS.CANCEL)}
              >
                <Button type="primary" shape="round" block style={{ height: 40 }} icon={<SendOutlined />}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_PUBLISH_BUTTON)}
                </Button>
              </Popconfirm>
            )}
            {(onEdit || onManageAvailability) && (
              <div style={{ display: 'grid', gridTemplateColumns: onEdit && onManageAvailability ? '1fr 1fr' : '1fr', gap: 8 }}>
                {onEdit && (
                  <Button
                    shape="round"
                    block
                    style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                    icon={<EditOutlined />}
                    onClick={onEdit}
                  >
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_EDIT_BUTTON)}
                  </Button>
                )}
                {onManageAvailability && (
                  <Button
                    shape="round"
                    block
                    style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                    icon={<CalendarOutlined />}
                    onClick={onManageAvailability}
                  >
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MANAGE_AVAILABILITY_BUTTON)}
                  </Button>
                )}
              </div>
            )}
            {onTakeOutOfService && !isParked && (
              <Button
                shape="round"
                block
                style={{ height: 40, border: 'none', ...pillToneStyle('warning', token) }}
                icon={<IconTool size={16} />}
                onClick={onTakeOutOfService}
              >
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OUT_OF_SERVICE_ACTION)}
              </Button>
            )}
            {onDelete && (
              <Button
                shape="round"
                block
                style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                icon={<DeleteOutlined />}
                onClick={() => setDeleteSheetOpen(true)}
              >
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
              </Button>
            )}
          </div>
        )}
      </div>

      {onDelete && (
        <ConfirmBottomSheet
          open={deleteSheetOpen}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_CONFIRM_TITLE)}
          description={space.title}
          cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_SHEET_KEEP)}
          confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_SHEET_CONFIRM)}
          onConfirm={() => {
            setDeleteSheetOpen(false);
            onDelete();
          }}
          onClose={() => setDeleteSheetOpen(false)}
        />
      )}
    </div>
  );
};

export default HeavyDutyMachineryCard;
